import React, { useState } from 'react';

import PropTypes from 'prop-types';

import clsx from 'clsx';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import {
    Button,
    PopoverSheet,
    Text,
} from 'reaxl';

/**
 * Array of day names in the order they appear in JavaScript's Date.getDay() method
 * @constant {string[]}
 */
const daysWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Gets the current day of the week as a string
 * @constant {string}
 */
const usersDay = daysWeek[new Date().getDay()];

/**
 * Formats operating hours into a readable string format
 *
 * @param {string} [open=''] - The opening time (e.g., "9:00 AM")
 * @param {string} [close=''] - The closing time (e.g., "5:00 PM")
 * @returns {string} Formatted hours string (e.g., "9:00 AM - 5:00 PM") or "Closed"
 */
const formatOperatingHours = (open = '', close = '') => (open.toLowerCase() === 'closed' ? 'Closed' : [open, close].join(' - '));

/**
 * Displays the current operating status (Open/Closed) and closing time if open
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.hours - Array of operating hour objects with label, open, and close properties
 * @param {boolean} props.isDealerOpen - Whether the dealer is currently open
 * @returns {React.ReactElement} The current hours of operation display
 */
function CurrentHoursOfOperation({ hours, isDealerOpen, isInline }) {
    const { close } = hours.find((day) => day.label === usersDay);

    const openCloseProps = {
        color: isDealerOpen ? 'success' : 'subdued-lightest',
        weight: isDealerOpen ? 'bold' : 'normal',
    };

    const renderBullet = isInline || isDealerOpen;

    return (
        <>
            <Text {...openCloseProps}>
                {isDealerOpen ? 'Open' : 'Closed'}
            </Text>
            {renderBullet && (
                <Text
                    size={300}
                    weight="bold"
                >
                    &bull;
                </Text>
            )}
            {isDealerOpen && (
                <Text weight="normal">{`Closes at ${close}`}</Text>
            )}
        </>
    );
}

CurrentHoursOfOperation.propTypes = {
    /**
     * Array of operating hour objects, which has the dealer's open/close times, as well as the label for the day of the week
     */
    hours: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        open: PropTypes.string,
        close: PropTypes.string.isRequired,
    })).isRequired,

    /**
     * Boolean representing if the dealer is currently open or not
     */
    isDealerOpen: PropTypes.bool.isRequired,
};

/**
 * Main Operating Hours component that displays current dealer status and provides access to full hours
 *
 * This component shows the current open/closed status of a dealer and provides a "View all hours"
 * button that opens a popover with the complete weekly schedule. The display adapts to different
 * screen sizes using responsive design patterns.
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS class name to apply to the root element
 * @param {Array<Object>} props.hours - Array of operating hour objects containing label, open, and close properties
 * @param {boolean} [props.isDealerOpen=false] - Whether the dealer is currently open
 * @param {Object} [props.rest] - Additional props spread to the root div element
 * @returns {React.ReactElement|boolean} The operating hours component or false if no hours provided
 */
function OperatingHours({ hours, isDealerOpen = false, isInline = false, ...rest }) {
    const [showPopover, setShowPopover] = useState(false);
    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    const hoursOfOperation = hours?.map(({ label, open, close }) => {
        const formattedHours = formatOperatingHours(open, close);

        return (
            <div
                className="display-flex justify-content-between gap-5"
                key={label}
                data-day={label}
            >
                <Text size={300}>{label}</Text>
                <Text size={300}>{formattedHours}</Text>
            </div>
        );
    });

    return !!hours && (
        <div
            className={clsx('display-flex gap-2', {
                'flex-wrap justify-content-center': !isInline,
            })}
            data-cmp="operatingHours"
            {...rest}
        >
            <CurrentHoursOfOperation
                hours={hours}
                isDealerOpen={isDealerOpen}
                isInline={isInline}
            />
            <div className={!isInline ? 'text-center w-100' : ''}>
                <Button
                    bsStyle="text"
                    onClick={() => setShowPopover(true)}
                    id="view-all-hours"
                >
                    View all hours
                </Button>
                <PopoverSheet
                    borderColor="neutral-secondary"
                    fullWidth={false}
                    show={showPopover}
                    onHide={() => setShowPopover(false)}
                    position="center"
                    heading="Hours of Operation"
                >
                    {!isXs && (
                        <Text
                            className="display-block margin-bottom-2"
                            weight="bold"
                        >
                            Hours of Operation
                        </Text>
                    )}
                    {hoursOfOperation}
                </PopoverSheet>
            </div>
        </div>
    );
}

OperatingHours.propTypes = {
    /**
     * Additional CSS class name to apply to the root element for custom styling
     */
    className: PropTypes.string,
    /**
     * Array of operating hour objects that define the dealer's weekly schedule.
     * Each object should contain:
     * - label: string (day of the week, e.g., "Monday")
     * - open: string (opening time, e.g., "9:00 AM" or "Closed")
     * - close: string (closing time, e.g., "5:00 PM" or "Closed")
     */
    hours: PropTypes.array,
    /**
     * Boolean flag indicating whether the dealer is currently open for business.
     * Used to determine the display text and styling of the current status.
     */
    isDealerOpen: PropTypes.bool,
};

export default OperatingHours;
